import { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, IconButton, TextField, FormHelperText } from '@mui/material';

import {
  FORTA_BLACK,
  FORTA_BLUE,
  FORTA_ERROR,
  FORTA_SUCCESS,
  FORTA_WHITE,
} from '../../utils/colors';
import { getThemeColor } from '../../utils/common';
import ExpandDialog from './dialogs/ExpandDialog';
import { Themes } from '../../constants';
import ExpandIcon from '../svgs/ExpandIcon';

import type { StandardTextFieldProps, FormHelperTextProps } from '@mui/material';
import type { ChangeEvent } from 'react';

interface FHTextareaProps extends StandardTextFieldProps {
  colorTheme?: Themes;
  coloredBackground?: boolean;
  expandable?: boolean;
  error?: boolean;
  poperMessage?: string;
  minLength?: number;
  fullHeight?: boolean;
  onEdit?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  onCloseExpandDialog?: () => void;
}

const StyledTextarea = styled(TextField, {
  shouldForwardProp: (prop) =>
    prop !== 'error' &&
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'fullHeight',
})<FHTextareaProps>(({ colorTheme, coloredBackground, error, fullHeight }: any) => ({
  backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
  borderRadius: '8px',
  border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
  '&:focus-within': {
    border: error ? `2px solid ${FORTA_ERROR}` : `2px solid ${FORTA_SUCCESS}`,
  },
  padding: '8px',
  boxShadow: fullHeight
    ? '0px 2px 2px rgba(0, 0, 0, 0.25)'
    : `0px 2px 2px ${getThemeColor(colorTheme).light2}`,
  '& .MuiInputBase-root': {
    color: FORTA_BLACK,
    paddingLeft: '0.5em',
    paddingRight: fullHeight ? '0.5em' : '1.7em',
    paddingTop: '0.5em',
    fontSize: 16,
    height: fullHeight ? '100%' : 'initial',

    '& .MuiInputBase-inputMultiline': {
      height: fullHeight ? '100%!important' : 'initial',
    },

    '& ::-webkit-scrollbar': {
      width: '12px',
    },

    '& ::-webkit-scrollbar-thumb': {
      visibility: fullHeight ? 'visible' : 'hidden',
    },

    '& ::-webkit-scrollbar-track': {
      visibility: fullHeight ? 'visible' : 'hidden',
    },

    '&:before': {
      display: 'none',
    },
    '&:after': {
      display: 'none',
    },

    '& .MuiInput-input.Mui-disabled': {
      WebkitTextFillColor: FORTA_BLACK,
    },

    '& .MuiInput-input': {
      overflowX: 'hidden',
      overflowY: 'auto',
    },
  },
  '& .MuiFormHelperText-root': {
    position: 'absolute',
    bottom: 0,
    backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
    color: getThemeColor(colorTheme).primary,
    fontWeight: 'bold',
    paddingLeft: '2.5em',
    marginBottom: '0.25em',
    fontSize: '1em',
    borderRadius: 10,
  },
}));

const StyledFormHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) =>
    prop !== 'error' &&
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'fullHeight',
})<FormHelperTextProps>(({ colorTheme, error }: any) => ({
  color:
    error === null || error === undefined
      ? `${getThemeColor(colorTheme).primary}`
      : error === false
        ? `${FORTA_SUCCESS}`
        : `${FORTA_ERROR}`,
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '21px',
}));

const FHTextarea = (props: FHTextareaProps) => {
  const {
    fullWidth = true,
    fullHeight = false,
    minRows = 7,
    maxRows = 7,
    colorTheme = Themes.Primary,
    coloredBackground = false,
    expandable = false,
    error = false,
    placeholder,
    value = '',
    poperMessage = '',
    minLength = 0,
    disabled = false,
    helperText,
    onEdit,
    onCloseExpandDialog,
    ...textAreaProps
  } = props;
  const [openExpandDialog, setOpenExpandDialog] = useState<boolean>(false);

  return (
    <Box sx={{ position: 'relative', flexGrow: 1 }}>
      <StyledTextarea
        {...textAreaProps}
        placeholder={placeholder}
        disabled={disabled}
        variant="standard"
        multiline
        fullWidth={fullWidth}
        fullHeight={fullHeight}
        minRows={minRows}
        maxRows={maxRows}
        colorTheme={colorTheme}
        coloredBackground={coloredBackground}
        value={value}
        onChange={onEdit}
        error={error}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end',
          alignItems: 'center',
        }}
      >
        <StyledFormHelperText error={error}>{helperText}</StyledFormHelperText>
      </Box>
      {expandable && (
        <IconButton
          disableRipple
          sx={{ position: 'absolute', right: 0, top: 5 }}
          onClick={() => setOpenExpandDialog(true)}
        >
          <ExpandIcon color={getThemeColor(colorTheme).primary} />
        </IconButton>
      )}

      <ExpandDialog
        open={openExpandDialog}
        colorTheme={colorTheme}
        title={placeholder}
        poperMessage={poperMessage}
        minLength={minLength}
        disabled={disabled}
        value={value}
        onClose={() => {
          setOpenExpandDialog(false);
          if (onCloseExpandDialog) {
            onCloseExpandDialog();
          }
        }}
        onEdit={onEdit}
      />
    </Box>
  );
};

export default FHTextarea;
